/**
 * CpaClient — CPA (Credit Program Advisor, backend SalesRecommendationHub) Smithy TS client with SigV4 auth.
 *
 * Serves the BD-facing questionnaire flow (catalog, draft, submit, recommendations) and the read-only
 * investment program configuration behind the admin 项目参数 tab (Deal Desk admins only). The admin
 * decision-tree editor in this module keeps its own hand-written types (see ../types.ts).
 *
 * Auth flow (mirrors IvtHubClient):
 *   Federate id_token → Cognito Identity Pool (appConfig.identityPoolId) → IAM temp credentials → SigV4
 *   + the same id_token as `X-Id-Token`, which the service validates for the caller's identity and
 *   Deal Desk Tool access (the SigV4 principal is the shared agentcore-backend role).
 *
 * The API lives in the aws partition in us-east-1, unlike the portal's other backends (us-west-2), so
 * requests are signed for {@link CPA_REGION} while the Cognito pool keeps `appConfig.region`.
 *
 * Usage:
 *   import { getCpaClient, GetAllQuestionCandidatesCommand } from './CpaClient';
 *   const out = await getCpaClient().send(new GetAllQuestionCandidatesCommand({}));
 */
import { SalesRecommendationHubClient } from '@amzn/salesrecommendationhub-client';
import type { SalesRecommendationHubClientConfig } from '@amzn/salesrecommendationhub-client';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import { HttpRequest } from '@smithy/protocol-http';
import { appConfig } from '../../config';
import { getIdToken } from '../../context/AuthContext';

export {
  SalesRecommendationHubClient,
  CreateQuestionnaireCommand,
  GetQuestionnaireCommand,
  ListQuestionnaireCommand,
  GetNextQuestionCommand,
  SubmitQuestionnaireCommand,
  GetQuestionnaireRecommendationsCommand,
  GetQuestionCandidatesCommand,
  GetAllQuestionCandidatesCommand,
  ListInvestmentProgramsCommand,
  GetInvestmentProgramCommand,
  CreateInvestmentProgramCommand,
  UpdateInvestmentProgramCommand,
  GetRecommendationRulesCommand,
  ListRecommendationRuleVersionsCommand,
  CreateRuleDraftCommand,
  UpdateRuleDraftCommand,
  DeleteRuleDraftCommand,
  SubmitRuleVersionCommand,
  RejectRuleVersionCommand,
  PublishRuleVersionCommand,
  UpdateRuleVersionEngineSyncCommand,
  EchoCommand,
} from '@amzn/salesrecommendationhub-client';

export type {
  Answer,
  AnswerOption,
  AutoAnswerRule,
  DiscountMethod,
  DiscountTableRow,
  ExpressionRule,
  InvestmentProgramData,
  InvestmentProgramSettings,
  MultiLanguageText,
  QuestionnaireData,
  QuestionnaireNode,
  Recommendation,
  RecommendationProgram,
  RuleData,
  RuleParamData,
  RuleSectionData,
  RuleVersionData,
  RuleVersionSummary,
} from '@amzn/salesrecommendationhub-client';

/** Region the Credit Program Advisor API is deployed to and signed for (fixed platform fact, not per stage). */
export const CPA_REGION = 'us-east-1';

let _credentialsProvider: ReturnType<typeof fromCognitoIdentityPool> | null = null;

function createCredentialsProvider() {
  if (!_credentialsProvider) {
    _credentialsProvider = fromCognitoIdentityPool({
      identityPoolId: appConfig.identityPoolId,
      logins: {
        [appConfig.identityProvider]: () => {
          const token = getIdToken();
          if (!token) throw new Error('No id_token available');
          return Promise.resolve(token);
        },
      },
      clientConfig: { region: appConfig.region },
    });
  }
  return _credentialsProvider;
}

/**
 * Builds a client for `endpoint`: signs for us-east-1 and adds the caller's id_token as `X-Id-Token`.
 * The header is added in the `build` step, which runs before SigV4 signing, so it is covered by the
 * signature. `overrides` exists for tests (static credentials, stub request handler).
 */
export function createCpaClient(
  endpoint: string,
  overrides: Partial<SalesRecommendationHubClientConfig> = {},
): SalesRecommendationHubClient {
  const client = new SalesRecommendationHubClient({
    endpoint,
    region: CPA_REGION,
    credentials: createCredentialsProvider(),
    ...overrides,
  });
  client.middlewareStack.add(
    (next) => async (args) => {
      if (HttpRequest.isInstance(args.request)) {
        const token = getIdToken();
        if (token) args.request.headers['X-Id-Token'] = token;
      }
      return next(args);
    },
    { step: 'build', name: 'cpaIdTokenHeader', priority: 'high' },
  );
  return client;
}

let _client: SalesRecommendationHubClient | null = null;

/**
 * Throws fast (before caching) if the stage has no Credit Program Advisor endpoint configured, so a
 * bad endpoint is not resolved as a URL and cached for the rest of the session.
 */
export function getCpaClient(): SalesRecommendationHubClient {
  if (!appConfig.cpaApiEndpoint || appConfig.cpaApiEndpoint === 'TBD') {
    throw new Error(
      `CPA API endpoint is not configured for stage "${appConfig.stage}".`,
    );
  }
  if (!_client) {
    _client = createCpaClient(appConfig.cpaApiEndpoint);
  }
  return _client;
}

/** Reset the client (e.g. after re-login when credentials expire). */
export function resetCpaClient(): void {
  if (_client) {
    _client.destroy();
    _client = null;
  }
  _credentialsProvider = null;
}
